import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface FkMultiSelectOption {
  value: number;
  label: string;
}

interface Props {
  options: FkMultiSelectOption[];
  value: number[];
  onChange: (v: number[]) => void;
  placeholder?: string;
}

/**
 * Multi-select FK picker. Same plain-Popover approach as FkCombobox —
 * cmdk dropped non-current options in admin's render context.
 */
export function FkMultiSelect({ options, value, onChange, placeholder = 'Select...' }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = options.filter((o) => value.includes(o.value));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = (id: number) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex flex-wrap items-center gap-1 min-h-[36px] px-2 py-1 border rounded-md hover:bg-muted text-left w-full"
        >
          {selected.length === 0 ? (
            <span className="text-muted-foreground text-sm">{placeholder}</span>
          ) : (
            selected.map((o) => (
              <Badge key={o.value} variant="secondary" className="gap-1">
                {o.label}
                <button
                  type="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(o.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      toggle(o.value);
                    }
                  }}
                  className="cursor-pointer hover:bg-muted-foreground/20 rounded-sm"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-1" align="start">
        <Input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          className="h-8 mb-1"
        />
        <div className="max-h-72 overflow-y-auto flex flex-col">
          {filtered.length === 0 ? (
            <div className="py-3 text-center text-sm text-muted-foreground">No matches.</div>
          ) : (
            filtered.map((o) => {
              const checked = value.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o.value)}
                  className={cn(
                    'flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted',
                    checked && 'bg-muted/60'
                  )}
                >
                  <input type="checkbox" checked={checked} readOnly className="mr-1 shrink-0" />
                  <span className="truncate flex-1">{o.label}</span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
